import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useLocalSearchParams } from "expo-router";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { z } from "zod";
import { Layout } from "~/components/Layout";
import { Screen } from "~/components/Screen";
import { Spacer } from "~/components/Spacer";
import { ButtonV2 } from "~/components/V2/Button";
import { ErrorViewV2 } from "~/components/V2/ErrorViewV2";
import { Field } from "~/components/V2/Field";
import { Container } from "~/components/V2/PageContainer";
import { SpinnerV2 } from "~/components/V2/SpinnerV2";
import { BodyText } from "~/components/V2/Text/BodyText";
import { BrandText } from "~/components/V2/Text/BrandText";
import { View } from "~/components/V2/View";
import { useStore } from "~/mobx/utils/useStore";
import { assert } from "~/utils/assert";
import { BreedPickerModal } from "./BreedPickerModal";
import { InsuranceCompanyPickerModal } from "./InsuranceCompanyPickerModal";
import { InsuranceNumberInputModal } from "./InsuranceNumberInputModal";

export const PetDetailsScreen = observer(function PetDetailsScreen() {
  const [isBreedModalVisible, setIsBreedModalVisible] = useState(false);
  const [isInsuranceCompanyModalVisible, setIsInsuranceCompanyModalVisible] =
    useState(false);
  const [
    isInsuranceNumberInputModalVisible,
    setIsInsuranceNumberInputModalVisible,
  ] = useState(false);

  const store = useStore();
  const activeUser = store.authStore.activeUser;
  const params = useLocalSearchParams<{ id: string }>();
  const id = params.id;

  assert(activeUser, "Active user should be defined");

  const query = useQuery({
    queryKey: ["pet", id],
    queryFn: async () => {
      const result = await store.petsStore.getPet(id);
      return result;
    },
  });

  if (query.isLoading) {
    return (
      <Screen withBottomInset withTopInset preventScroll>
        <Layout>
          <Container>
            <View flex centerContent>
              <SpinnerV2 />
            </View>
          </Container>
        </Layout>
      </Screen>
    );
  }

  if (query.isError) {
    return (
      <Screen withBottomInset withTopInset preventScroll>
        <View flex centerContent paddingHorizontal24>
          <ErrorViewV2
            error={query.error}
            title={intl.formatMessage({
              id: "ZFx66U",
              defaultMessage: "Something went wrong",
              description:
                "Error message title when fetching auth session URL fails",
            })}
          />
          <ButtonV2
            onPress={query.refetch}
            title={intl.formatMessage({
              id: "T4E2Fv",
              defaultMessage: "Try Again!",
              description: "Button to retry fetching auth session URL",
            })}
          />
        </View>
      </Screen>
    );
  }

  const pet = query.data;

  assert(pet, "Pet should be defined");

  return (
    <Screen withBottomInset withTopInset preventScroll>
      <Layout>
        <Container
          title={undefined}
          styleBottomView={{
            paddingHorizontal: 0,
          }}
        >
          {pet.species?.id && (
            <BreedPickerModal
              pet={pet}
              initialBreed={pet.breed}
              isVisible={isBreedModalVisible}
              onClose={() => {
                setIsBreedModalVisible(false);
              }}
            />
          )}
          <InsuranceCompanyPickerModal
            pet={pet}
            isVisible={isInsuranceCompanyModalVisible}
            onClose={() => {
              setIsInsuranceCompanyModalVisible(false);
            }}
            initialInsuranceCompanyId={pet.insuranceCompany?.id}
          />

          <InsuranceNumberInputModal
            initialInsuranceNumber={pet.insuranceNumber}
            pet={pet}
            isVisible={isInsuranceNumberInputModalVisible}
            onClose={() => {
              setIsInsuranceNumberInputModalVisible(false);
            }}
          />

          <View flex>
            <View paddingHorizontal24>
              <BrandText sizeH2 weightExtraBold colorBlack>
                {pet.name}
              </BrandText>
              <Spacer small />

              {/* Labrador retriever – 3 years, 10 months */}
              {!!pet.dateOfBirth && (
                <BodyText sizeLarge weightRegular colorGrey>
                  {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                  {`${pet.breed?.name} - ${generateFormattedAgeString(pet.dateOfBirth)}`}
                </BodyText>
              )}
            </View>
            <Spacer extraExtraLarge />

            {/* Subtitle */}
            <View paddingHorizontal24 paddingVertical8>
              <BrandText sizeH3 weightBold colorBlack>
                {intl.formatMessage({
                  id: "Qlxw7J",
                  defaultMessage: "Pet details",
                  description: "Title for pet details",
                })}
              </BrandText>
            </View>

            {/* Name */}
            <Field
              value={pet.name}
              label={intl.formatMessage({
                id: "qTE/PL",
                defaultMessage: "Name",
                description: "Label for pet name",
              })}
            />
            {/* Date of birth */}
            <Field
              value={dayjs(pet.dateOfBirth).format("YYYY-MM-DD")}
              label={intl.formatMessage({
                id: "WoIxoP",
                defaultMessage: "Date Of Birth",
                description: "Label for pet date of birth",
              })}
            />
            {/* Species */}
            <Field
              value={pet.species?.name}
              label={intl.formatMessage({
                id: "z0tayl",
                defaultMessage: "Species",
                description: "Label for pet species",
              })}
            />
            {/* Breed */}
            <Field
              onPress={() => {
                setIsBreedModalVisible(true);
              }}
              value={pet.breed?.name}
              label={intl.formatMessage({
                id: "NmI5fB",
                defaultMessage: "Breed",
                description: "Label for pet breed",
              })}
              isEditable
            />
            {/* Gender */}
            <Field
              value={
                pet.gender?.id === "1"
                  ? intl.formatMessage({
                      id: "8rOQmk",
                      defaultMessage: "Male",
                      description: "Male gender label",
                    })
                  : intl.formatMessage({
                      id: "95SGnw",
                      defaultMessage: "Female",
                      description: "Female gender label",
                    })
              }
              label={intl.formatMessage({
                id: "SwYEPV",
                defaultMessage: "Gender",
                description: "Label for pet gender",
              })}
            />
            {/* Microchip */}
            <Field
              value={pet.microchip}
              label={intl.formatMessage({
                id: "xyXEtx",
                defaultMessage: "Microchip",
                description: "Label for pet microchip",
              })}
              isLast
            />

            <Spacer extraExtraLarge />

            {/* Subtitle */}
            <View paddingHorizontal24 paddingVertical8>
              <BrandText sizeH3 weightExtraBold colorBlack>
                {intl.formatMessage({
                  id: "v2562G",
                  defaultMessage: "Insurance details",
                  description: "Title for insurance details",
                })}
              </BrandText>
            </View>

            {/* Insurance Company */}
            <Field
              value={pet.insuranceCompany?.name}
              label={intl.formatMessage({
                id: "fRA8IF",
                defaultMessage: "Insurance provider",
                description: "Label for pet insurance company",
              })}
              isEditable
              onPress={() => {
                setIsInsuranceCompanyModalVisible(true);
              }}
            />
            {/* Insurance Number */}
            <Field
              value={pet.insuranceNumber}
              label={intl.formatMessage({
                id: "zDOf+o",
                defaultMessage: "Insurance No",
                description: "Label for pet insurance number",
              })}
              isLast
              isEditable
              onPress={() => {
                setIsInsuranceNumberInputModalVisible(true);
              }}
            />
          </View>
        </Container>
      </Layout>
    </Screen>
  );
});

function generateFormattedAgeString(dateOfBirth: string) {
  const isPast = dayjs(dateOfBirth).isBefore(dayjs());

  if (!isPast) {
    throw new Error("Date of birth should be in the past");
  }

  const monthsOld = Math.abs(dayjs().diff(dayjs(dateOfBirth), "months"));

  const yearsOld = Math.floor(monthsOld / 12);
  const remainingMonthsOld = Math.floor(monthsOld % 12);

  z.number().min(0).parse(yearsOld);
  z.number().min(0).max(11).parse(remainingMonthsOld);

  let result = "";

  const monthLabelSingular = intl.formatMessage({
    id: "tAIqPC",
    defaultMessage: "month",
    description: "Label for singular month",
  });

  const monthLabelPlural = intl.formatMessage({
    id: "VNdtgp",
    defaultMessage: "months",
    description: "Label for plural months",
  });

  const yearLabelSingular = intl.formatMessage({
    id: "ysoTKU",
    defaultMessage: "year",
    description: "Label for singular year",
  });

  const yearLabelPlural = intl.formatMessage({
    id: "qdUnYJ",
    defaultMessage: "years",
    description: "Label for plural years",
  });

  if (yearsOld > 0) {
    result += `${yearsOld} ${yearsOld === 1 ? yearLabelSingular : yearLabelPlural}, `;
  }

  if (remainingMonthsOld > 0) {
    result += `${remainingMonthsOld} ${
      remainingMonthsOld === 1 ? monthLabelSingular : monthLabelPlural
    }`;
  }

  return result;
}
